import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_TONES,
  CampaignCard,
  CampaignCardAction,
  CampaignCardBody,
  CampaignCardHeader,
} from '#/entities/campaign'
import type { CampaignAudience, CampaignRailRow } from '#/entities/campaign'
import { cn } from '#/shared/lib/utils'

import { CampaignStrategyChips } from './campaign-strategy-chips'

function RailRows({ rows }: { rows: CampaignRailRow[] }) {
  return (
    <>
      {rows.map((row) => (
        <div key={row.key} className="flex items-center justify-between gap-3">
          <span className="text-xs text-gray-500">{row.label}</span>
          <span
            className={cn(
              'text-xs font-medium',
              CAMPAIGN_TONES[row.tone ?? 'default'],
            )}
          >
            {row.value}
          </span>
        </div>
      ))}
    </>
  )
}

interface CampaignDetailRailProps {
  campaignId: string
  placement: CampaignRailRow[]
  placementNote: string
  audience: CampaignAudience | null
  cost: CampaignRailRow[]
  onEditAudience: () => void
}

export function CampaignDetailRail({
  campaignId,
  placement,
  placementNote,
  audience,
  cost,
  onEditAudience,
}: CampaignDetailRailProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-4">
      <CampaignCard>
        <CampaignCardHeader
          dense
          label={t('campaigns.detail.sections.placement')}
        />
        <CampaignCardBody dense>
          <RailRows rows={placement} />
          <p className="text-xs text-gray-300">{placementNote}</p>
        </CampaignCardBody>
      </CampaignCard>

      {audience != null && (
        <CampaignCard>
          <CampaignCardHeader
            dense
            label={t('campaigns.detail.sections.strategy')}
            action={
              <CampaignCardAction onClick={onEditAudience}>
                {t('campaigns.detail.actions.edit')}
              </CampaignCardAction>
            }
          />
          <CampaignCardBody dense>
            <p className="flex flex-wrap items-baseline gap-2">
              <span className="text-base font-semibold text-gray-900">
                {audience.enrolled}
              </span>
              <span className="text-xs text-gray-500">{audience.matched}</span>
            </p>
            <CampaignStrategyChips campaignId={campaignId} />
            <p className="text-xs text-gray-300">{audience.note}</p>
          </CampaignCardBody>
        </CampaignCard>
      )}

      <CampaignCard>
        <CampaignCardHeader dense label={t('campaigns.detail.sections.cost')} />
        <CampaignCardBody dense>
          <RailRows rows={cost} />
        </CampaignCardBody>
      </CampaignCard>
    </div>
  )
}
